import React, { useState } from 'react';
import { CheckCircle, FileEarmarkArrowUp } from 'react-bootstrap-icons';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

import T from 'utils/useLocalizer';
import OrderSendInvoiceModal from './modals/OrderSendInvoiceModal';
import useNotify from 'utils/useNotify';
import { useSendOrderInvoice } from 'services/orders/ordersServices';
import TimeAgo from 'components/ui/timeAgo/TimeAgo';
import styles from './style.module.scss';
import { ISendInvoiceFormData, ISendInvoiceRequestData } from 'models/ordersModel';
import { useNavigate } from 'react-router-dom';
import { pageRoutes } from 'config/router/pageRoutes';
import { buttons } from 'utils/resources';

dayjs.extend(relativeTime);

interface OrderInvoiceListItemProps {
    orderId: number;
    orderNumber: string;
    isSent: boolean;
    sentOn?: string;
    recipientPhoneNumber: string;
    hasNotificationPhoneNumber: boolean;
    hasInvoiceTemplate: boolean;
}

const NotifyContent = (message: string, onClick: () => void, settingsButtonText: string) => {
    return <div>
        {message}
        <button className='btn btn-secondary ms-2' onClick={(e) => { e.stopPropagation(); onClick(); }}>{settingsButtonText}</button>
    </div>
}

const OrderInvoiceListItem: React.FC<OrderInvoiceListItemProps> = ({ orderId, orderNumber, isSent, sentOn, recipientPhoneNumber, hasNotificationPhoneNumber, hasInvoiceTemplate }) => {
    const { mutateAsync: sendInvoiceAsync, isLoading } = useSendOrderInvoice()
    const { notifySuccess, notifyError, notifyInfo } = useNotify();
    const navigate = useNavigate();

    const [openModal, setOpenModal] = useState(false);
    const [sentTime, setSentTime] = useState<string>(sentOn ? dayjs.utc(sentOn).toISOString() : '');

    const resources = {
        settingsButtonText: T('order.setting.btn'),
        noNotificationPhone: T('order.invoice.send.no.notification.phone'),
        noInvoiceTemplate: T('order.invoice.send.no.invoice.template'),
        invoiceSentSuccess: T('order.invoice.send.success'),
        invoiceSentError: T('order.invoice.send.error'),
        sentLabel: T('order.invoice.sent'),
        sendInvoiceButton: T('order.invoice.send.btn'),
        resend: T(buttons.resend),
    }

    const handleOpenModal = () => {
        if (!hasNotificationPhoneNumber) {
            notifyInfo(NotifyContent(resources.noNotificationPhone, () => navigate(pageRoutes.orderSettings), resources.settingsButtonText));
            return;
        }
        if (!hasInvoiceTemplate) {
            notifyInfo(NotifyContent(resources.noInvoiceTemplate, () => navigate(pageRoutes.orderSettings), resources.settingsButtonText));
            return;
        }
        setOpenModal(true);
    };

    const handleCloseModal = () => {
        setOpenModal(false);
    };

    const handleSendInvoice = (data: ISendInvoiceFormData) => {
        if (!data.invoiceFile) return;

        const invoiceData: ISendInvoiceRequestData = {
            orderId,
            invoiceFile: data.invoiceFile,
            recipientNumber: recipientPhoneNumber,
        };
        sendInvoiceAsync(invoiceData)
            .then((res) => {
                if (res) {
                    notifySuccess(resources.invoiceSentSuccess);
                    setSentTime(dayjs().toISOString());
                }
                else {
                    notifyError(resources.invoiceSentError);
                }
            })
            .catch((err: any) => {
                console.error('Failed to send invoice', err);
            })
            .finally(() => {
                handleCloseModal();
            })
    };

    return (
        <div>
            {sentTime !== '' || (isSent && sentOn) ?
                <div className='d-flex gap-2'>
                    <div className={`${styles.completedStatus} d-flex align-items-center rounded-pill p-2 f-s-12`}>
                        <CheckCircle className='me-2' />
                        <span>{resources.sentLabel} <TimeAgo sentTime={sentTime} /></span>
                    </div>
                    <button className={`btn btn-link f-s-12 ${styles.InvoiceSendButton}`} onClick={handleOpenModal}>
                        <span className='text-muted'>{resources.resend}</span>
                    </button>
                </div>
                :
                <div className='not-sent'>
                    <button className={`btn btn-link f-s-14 ${styles.InvoiceSendButton}`} onClick={handleOpenModal}>
                        <FileEarmarkArrowUp className='me-2' /><span>{resources.sendInvoiceButton}</span>
                    </button>
                </div>}

            <OrderSendInvoiceModal
                orderNumber={orderNumber}
                show={openModal}
                isLoading={isLoading}
                onSubmit={handleSendInvoice}
                onClose={handleCloseModal}
            />
        </div>
    );
};

export default OrderInvoiceListItem;
